/**
 * Section A6 Page
 * A.6.參與方
 * DataTable CRUD 模式 (參照 UserRolesPage 排版)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionA6Service, { SectionA6Detail, SectionA6Create, SectionA6Update } from '../services/sectionA6Service';
import { getSystemCodesByType, SystemCode } from '../services/systemCodeService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionA6Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [dataList, setDataList] = useState<SectionA6Detail[]>([]);
  const [partyOptions, setPartyOptions] = useState<SystemCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========== Modal 狀態 ==========
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<SectionA6Detail | null>(null);
  const [formData, setFormData] = useState<{
    party_type: number;
    party_name: number;
    activity_participant: string;
  }>({
    party_type: 1,
    party_name: 0,
    activity_participant: ''
  });

  // ========== 分頁 ==========
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // ========== 功能說明文字（固定英文） ==========
  const descLine1 = "Select the type of Party ('Host Party' or 'Other participating Party') and the name of the Party from the drop list menu.";
  const descLine2 = "Provide the names of the activity participants to be authorised by the respective Parties.";

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionA6Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 載入締約方名稱選項 ==========
  useEffect(() => {
    const loadPartyOptions = async () => {
      try {
        const codes = await getSystemCodesByType('Article 6.4 Party');
        setPartyOptions(codes);
      } catch (err) {
        console.error('載入締約方選項失敗:', err);
      }
    };
    loadPartyOptions();
  }, []);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setDataList([]);
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await sectionA6Service.getListByProject(selectedProjectId);
        setDataList(data);
      } catch (err: any) {
        console.error('載入 A.6 資料失敗:', err);
        setError(err.response?.data?.detail || (i18n.language === 'zh-TW' ? '載入資料失敗' : 'Failed to load data'));
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId, i18n.language]);

  // ========== 分頁計算 ==========
  const totalPages = Math.ceil(dataList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = dataList.slice(startIndex, startIndex + itemsPerPage);

  // ========== Modal 操作 ==========
  const openModal = (item?: SectionA6Detail) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        party_type: item.party_type,
        party_name: item.party_name,
        activity_participant: item.activity_participant || ''
      });
    } else {
      setEditingItem(null);
      setFormData({
        party_type: 1,
        party_name: partyOptions.length > 0 ? partyOptions[0].id : 0,
        activity_participant: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  // ========== CRUD 操作 ==========
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProjectId) return;

    if (!formData.party_name) {
      toast.warning(i18n.language === 'zh-TW' ? '請選擇締約方名稱' : 'Please select a party name');
      return;
    }

    try {
      if (editingItem) {
        // 更新
        const updateData: SectionA6Update = {
          party_type: formData.party_type,
          party_name: formData.party_name,
          activity_participant: formData.activity_participant || undefined
        };
        await sectionA6Service.update(editingItem.id, updateData);
        toast.success(t('message.saveSuccess'));
      } else {
        // 新增
        const createData: SectionA6Create = {
          project_id: selectedProjectId,
          party_type: formData.party_type,
          party_name: formData.party_name,
          activity_participant: formData.activity_participant || undefined
        };
        await sectionA6Service.create(createData);
        toast.success(t('message.createSuccess'));
      }

      closeModal();
      // 重新載入
      const data = await sectionA6Service.getListByProject(selectedProjectId);
      setDataList(data);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (item: SectionA6Detail) => {
    const ok = await toast.confirm({
      title: i18n.language === 'zh-TW' ? '刪除確認' : 'Delete Confirmation',
      message: i18n.language === 'zh-TW' ? '確定要刪除此筆參與方資料嗎？' : 'Are you sure you want to delete this party record?',
      confirmText: i18n.language === 'zh-TW' ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) {
      return;
    }

    try {
      await sectionA6Service.delete(item.id);
      toast.success(t('message.deleteSuccess'));
      // 重新載入
      if (selectedProjectId) {
        const data = await sectionA6Service.getListByProject(selectedProjectId);
        setDataList(data);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || t('common.error');
      toast.error(errorMsg);
    }
  };

  // ========== 顯示工具函數 ==========
  const getPartyTypeName = (type: number): string => {
    if (i18n.language === 'zh-TW') {
      return type === 1 ? '主辦締約方' : '活動參與方';
    }
    return type === 1 ? 'Host Party' : 'Participating Party';
  };

  const getPartyName = (item: SectionA6Detail): string => {
    if (i18n.language === 'zh-TW') {
      return item.party_name_cname || item.party_name_ename || String(item.party_name);
    }
    return item.party_name_ename || item.party_name_cname || String(item.party_name);
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_a6', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canCreate = hasPermission('section_a6', 'create');
  const canUpdate = hasPermission('section_a6', 'update');
  const canDelete = hasPermission('section_a6', 'delete');

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_a6" />
        {selectedProjectId && canCreate && (
          <button className="btn-primary" onClick={() => openModal()}>
            {t('common.create')}
          </button>
        )}
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {selectedProjectId && (
        <div>
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : dataList.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
              {i18n.language === 'zh-TW'
                ? '尚無參與方資料，請點擊「新增」按鈕新增資料'
                : 'No party data yet. Click "Create" to add a new record.'}
            </div>
          ) : (
            <>
              <div className="data-table-container">
                <table className="data-table">
                  <thead className="table-header-dark-green">
                    <tr>
                      <th>{i18n.language === 'zh-TW' ? '參與方類型' : 'Party Type'}</th>
                      <th>{i18n.language === 'zh-TW' ? '締約方名稱' : 'Party Name'}</th>
                      <th>{i18n.language === 'zh-TW' ? '活動參與方' : 'Activity Participant'}</th>
                      <th>{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <span className={`status-badge ${item.party_type === 1 ? 'active' : 'inactive'}`}>
                            {getPartyTypeName(item.party_type)}
                          </span>
                        </td>
                        <td>{getPartyName(item)}</td>
                        <td dangerouslySetInnerHTML={{ __html: item.activity_participant || '-' }} />
                        <td className="actions">
                          {canUpdate && (
                            <button className="btn-edit" onClick={() => openModal(item)} style={{ marginRight: '12px' }}>
                              {t('common.edit')}
                            </button>
                          )}
                          {canDelete && (
                            <button className="btn-delete" onClick={() => handleDelete(item)}>
                              {t('common.delete')}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 分頁控制 */}
              {dataList.length > itemsPerPage && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    <label>
                      {i18n.language === 'zh-TW' ? '每頁顯示：' : 'Items per page: '}
                      <select value={itemsPerPage} onChange={(e) => { setItemsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}>
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      {i18n.language === 'zh-TW' ? '筆' : ''}
                    </label>
                    <span className="pagination-text">
                      {i18n.language === 'zh-TW'
                        ? `共 ${dataList.length} 筆資料，第 ${currentPage} / ${totalPages} 頁`
                        : `Total ${dataList.length} records, Page ${currentPage} / ${totalPages}`}
                    </span>
                  </div>
                  <div className="pagination-buttons">
                    <button className="btn-pagination" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>⟪</button>
                    <button className="btn-pagination" onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`btn-pagination ${currentPage === page ? 'active' : ''}`}
                        onClick={() => setCurrentPage(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button className="btn-pagination" onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
                    <button className="btn-pagination" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>⟫</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}

      {/* 新增/編輯 Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
            <div className="modal-header">
              <h2>
                {editingItem
                  ? (i18n.language === 'zh-TW' ? '編輯參與方' : 'Edit Party')
                  : (i18n.language === 'zh-TW' ? '新增參與方' : 'Add Party')}
              </h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>{i18n.language === 'zh-TW' ? '參與方類型' : 'Party Type'} *</label>
                    <select
                      value={formData.party_type}
                      onChange={(e) => setFormData({ ...formData, party_type: parseInt(e.target.value) })}
                      required
                    >
                      <option value={1}>{i18n.language === 'zh-TW' ? '主辦締約方 (Host Party)' : 'Host Party'}</option>
                      <option value={2}>{i18n.language === 'zh-TW' ? '活動參與方 (Participating Party)' : 'Participating Party'}</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>{i18n.language === 'zh-TW' ? '締約方名稱' : 'Party Name'} *</label>
                    <select
                      value={formData.party_name}
                      onChange={(e) => setFormData({ ...formData, party_name: parseInt(e.target.value) })}
                      required
                    >
                      <option value={0} disabled>
                        {i18n.language === 'zh-TW' ? '-- 請選擇 --' : '-- Please select --'}
                      </option>
                      {partyOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {i18n.language === 'zh-TW'
                            ? (option.code_cname || option.code_ename)
                            : (option.code_ename || option.code_cname)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group full-width">
                    <label>{i18n.language === 'zh-TW' ? '活動參與方' : 'Activity Participant'}</label>
                    <RichTextEditor
                      value={formData.activity_participant}
                      onChange={(html) => setFormData({ ...formData, activity_participant: html })}
                      placeholder={i18n.language === 'zh-TW' ? '請輸入活動參與方...' : 'Please enter activity participant...'}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  {t('common.cancel')}
                </button>
                <button type="submit" className="btn-primary">
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionA6Page;
